import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Lang, translations } from './translation';

type LangContext = {
  lang: Lang;
  setLang: (value: Lang) => void;
  toggle: () => void;
  t: (key: keyof typeof translations['hr']) => string;
};

const LanguageContext = createContext<LangContext | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize immediately from localStorage to avoid first-render mismatch
  const initialLang: Lang = (() => {
    const stored = window.localStorage.getItem('lang');
    return stored === 'en' ? 'en' : 'hr';
  })();
  const [lang, setLang] = useState<Lang>(initialLang);

  useEffect(() => {
    window.localStorage.setItem('lang', lang);
  }, [lang]);

  const value = useMemo<LangContext>(
    () => ({
      lang,
      setLang,
      toggle: () => setLang(prev => (prev === 'hr' ? 'en' : 'hr')),
      t: key => translations[lang][key],
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};